// Puente de SOLO LECTURA hacia `catalog`/`clients`/`iam` (plan.md §3.3). Encauza por
// infrastructure/ (constitution.md regla #3) el acceso hoy directo por axios (/countries,
// /clients) o vía *.api legado (platformsApi/clientsApi/usersApi), con zod.
// Retiro: getPlatforms ⇒ @modules/catalog; getClients/getCountries ⇒ @modules/clients;
// getUsersByPlatform ⇒ @modules/iam, cuando expongan lectura (registrado en ROADMAP.md).

import { z } from 'zod';
import apiClient from '@shared/api/http';
import type { ClientId, CountryId, PlatformId, UserId } from '../domain/ids';
import type { ClientOption, PlatformOption, UserOption } from '../domain/shared.types';
import type { Country } from '../domain/overtime/overtime.types';

const IdSchema = z.union([z.string(), z.number()]).transform(String);

// `/clients` responde paginado en v1 (`{ items, ... }`); el resto de estos catálogos responde
// array plano. Este helper acepta ambas formas sin duplicar cada parser.
const listOf = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (value) =>
      Array.isArray(value) ? value : ((value as { items?: unknown } | null)?.items ?? []),
    z.array(schema)
  );

const PlatformOptionDtoSchema = z.object({ id: IdSchema, name: z.string() });
const ClientOptionDtoSchema = z.object({ id: IdSchema, name: z.string() });
// v1 puede nombrar el ISO como `code` o `isoCode`: se aceptan ambos.
const CountryDtoSchema = z
  .object({
    id: IdSchema,
    name: z.string(),
    code: z.string().nullish(),
    isoCode: z.string().nullish(),
  })
  .transform((c) => ({ id: c.id, name: c.name, code: c.code ?? c.isoCode ?? '' }));
// v1 responde camelCase y ya NO devuelve `username` ni `phone` (G-15): ambos quedan opcionales y
// `username` cae al email como etiqueta estable para los selectores.
const UserOptionDtoSchema = z
  .object({
    id: IdSchema,
    firstName: z.string(),
    lastName: z.string(),
    username: z.string().nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .transform((u) => ({
    id: u.id,
    first_name: u.firstName,
    last_name: u.lastName,
    username: u.username ?? u.email ?? '',
    phone: u.phone,
  }));

export const referenceDataRepository = {
  async getPlatforms(): Promise<PlatformOption[]> {
    const { data } = await apiClient.get('/platforms');
    return listOf(PlatformOptionDtoSchema)
      .parse(data ?? [])
      .map((p) => ({ id: p.id as PlatformId, name: p.name }));
  },

  async getClients(): Promise<ClientOption[]> {
    const { data } = await apiClient.get('/clients');
    return listOf(ClientOptionDtoSchema)
      .parse(data ?? [])
      .map((c) => ({ id: c.id as ClientId, name: c.name }));
  },

  async getCountries(): Promise<Country[]> {
    const { data } = await apiClient.get('/countries');
    return listOf(CountryDtoSchema)
      .parse(data ?? [])
      .map((c) => ({ id: c.id as CountryId, name: c.name, code: c.code }));
  },

  async getUsersByPlatform(platformId: PlatformId, clientId: ClientId): Promise<UserOption[]> {
    const { data } = await apiClient.get(`/users/by-platform/${platformId}`, {
      params: { clientId },
    });
    return listOf(UserOptionDtoSchema)
      .parse(data ?? [])
      .map((u) => ({
        id: u.id as UserId,
        firstName: u.first_name,
        lastName: u.last_name,
        username: u.username,
        phone: u.phone ?? undefined,
      }));
  },
};
